"use client";

/**
 * Navigation shim over TanStack Router, replacing `next/navigation`.
 *
 * `useRouter` preserves the app's unsaved-changes guard (NavigationProxyContext):
 * every navigation is gated behind `window.confirm(tips)` when a guard message is
 * set, matching the previous next/navigation proxy.
 */
import {
  useRouter as useTanstackRouter,
  useLocation,
  useParams as useTanstackParams,
} from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { NavigationProxyContext } from "./NavigationProxyProvider";

export interface AppRouter {
  push: (url: string) => void;
  replace: (url: string) => void;
  back: () => void;
  forward: () => void;
  refresh: () => void;
  prefetch: (url: string) => void;
}

export function useRouter(): AppRouter {
  const router = useTanstackRouter();
  const queryClient = useQueryClient();
  const [tips] = useContext(NavigationProxyContext);
  const confirmNav = () =>
    tips === undefined ? true : window.confirm(tips);

  return {
    // Use router.navigate (not history.push) so the URL and the rendered route
    // commit together — history.push updates the URL first and lets the previous
    // route linger for a tick, which races clicks right after a navigation.
    push: (url) => {
      if (confirmNav()) void router.navigate({ href: url });
    },
    replace: (url) => {
      if (confirmNav()) void router.navigate({ href: url, replace: true });
    },
    back: () => {
      if (confirmNav()) router.history.back();
    },
    forward: () => {
      if (confirmNav()) router.history.forward();
    },
    // In the SPA, "refresh" re-reads server state: invalidate React Query (which
    // backs the page-level useQuery data) and re-run any active route loaders.
    refresh: () => {
      void queryClient.invalidateQueries();
      void router.invalidate();
    },
    prefetch: () => {
      /* noop — TanStack preloads on intent via <Link> */
    },
  };
}

/** Current pathname (no search), like next's usePathname. */
export function usePathname(): string {
  return useLocation({ select: (l) => l.pathname });
}

/**
 * Current search params as a URLSearchParams (supports `.get()`), like next's
 * useSearchParams.
 */
export function useSearchParams(): URLSearchParams {
  const searchStr = useLocation({ select: (l) => l.searchStr });
  return new URLSearchParams(searchStr);
}

/** All matched route params, like next's useParams. */
export function useParams<
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- callers pass T to type the returned params
  T extends Record<string, string | undefined> = Record<
    string,
    string | undefined
  >,
>(): T {
  // TanStack's `strict: false` option types are awkward to satisfy in a generic
  // wrapper; cast the hook to a loose signature — the runtime returns all params.
  const useLooseParams = useTanstackParams as (opts: {
    strict: false;
  }) => Record<string, string | undefined>;
  return useLooseParams({ strict: false }) as T;
}
